import {
  ChatCreateData,
  ChatWithShortInfo,
  CurrentChatData,
  MetaResponseChats,
  ResponseChatsWithMeta,
  SwitchChatArchiveData,
  UpdateChat
} from "@sms/shared/chat/chat.interface";
import { EventChannel, Task } from "redux-saga";
import { all, call, cancel, cancelled, delay, Effect, fork, put, select, take, takeEvery } from "redux-saga/effects";
import { Action } from "redux";
import { PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { ApplicationStore } from "../application.store";
import { clearMessages, getMessagesAction } from "../messages/messages.actions";
import { setNewChatModalIsOpen, setNewChatServiceModalIsOpen } from "../modals/modals.actions";

import chatEntryActions from "./chat.entry-actions";
import chatActions from "./chat.actions";
import { socketChats, createSocketEventsChatsChannel, connectSocketChat } from "./chat.sockets";
import chatService from "./chat.service";

function* initChats(): Generator<Effect | Generator, void> {
  try {
    const countNotifications: number = (yield call(chatService.fetchCountAllNotifications)) as number;
    yield put(chatEntryActions.fetchCountAllNotificationsSuccess(countNotifications));
  } catch (e) {
    yield put(chatEntryActions.fetchCountAllNotificationsError(0));
  }

  yield fork(fetchChats);
}

//FetchAllChats
function* fetchChats(): Generator<Effect | Generator, void> {
  const chats: ChatWithShortInfo[] = (yield select(
    (state: ApplicationStore) => state.chats.chats
  )) as ChatWithShortInfo[];

  const countChats: number = chats.length;

  const page: number | undefined = (
    (yield select((state: ApplicationStore) => state.chats.meta)) as MetaResponseChats | null
  )?.page;
  try {
    const chats: ResponseChatsWithMeta = (yield call(chatService.fetchList, {
      countChats,
      page
    })) as ResponseChatsWithMeta;

    yield call(handleRecievedChatsWithMetaGenerator, chats);
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
    }
  } finally {
    if (yield cancelled()) {
      yield put(chatActions.resetChats());
      yield put(chatActions.setMetaChats(null));
    }
  }
}

function* handleRecievedChatsWithMetaGenerator(data: ResponseChatsWithMeta): Generator<Effect | Generator, void> {
  yield put(chatActions.setChatsWithShortInfo(data.data));
  yield put(chatActions.setMetaChats(data.meta));

  const nextPage: boolean = (yield select((state: ApplicationStore) => state.chats.meta?.hasNextPage)) as boolean;

  if (nextPage) {
    yield delay(1000);
    yield fork(fetchChats);
  }
}

function* createChat(newChatData: ChatCreateData): Generator<Effect | Generator, void, void> {
  try {
    socketChats.emit("createChat", newChatData);

    if (newChatData.type === "default") {
      yield put(setNewChatModalIsOpen(false));
    } else {
      yield put(setNewChatServiceModalIsOpen(false));
    }
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
    }
  }
}

function* handleChangeCurrentChat(
  data: string | null
): Generator<Effect | Generator, void, CurrentChatData | null | EventChannel<any>> {
  try {
    yield put(clearMessages());
    console.log("Calling function getMessagesAction");
    if (data !== null) {
      yield put(getMessagesAction());
    }
  } catch (e) {
    if (e instanceof Error) {
      console.error("socket error:", e);
    }
  }
}

function switchChatToArchive(switchChatArchiveData: SwitchChatArchiveData): void {
  socketChats.emit("switchChatArchive", switchChatArchiveData);
}

function updateChatSaga(data: UpdateChat): void {
  socketChats.emit("updateChat", data);
}

function* connectSocketChatSaga(): Generator<Effect, void, Task> {
  let task: Task | null = null;

  try {
    yield call(connectSocketChat);

    task = yield fork(socketChatWatchers);
  } finally {
    if (yield cancelled()) {
      socketChats.close();
      if (task !== null) {
        yield cancel(task);
      }
    }
  }
}

function* socketChatWatchers(): Generator<Effect | Generator, void, EventChannel<any> | Action<any>> {
  const socketEventsChatsChannel: EventChannel<any> = (yield call(createSocketEventsChatsChannel)) as EventChannel<any>;

  try {
    while (true) {
      const action: Action<any> = (yield take(socketEventsChatsChannel)) as Action<any>;

      yield put(action);
    }
  } finally {
    if (yield cancelled()) {
      socketEventsChatsChannel.close();
    }
  }
}

function* displayNotifyNewMessage(data: ChatWithShortInfo): Generator {
  const selectChatId: string | null = yield select((state: ApplicationStore) => state.chats.selectChatId);

  if (selectChatId !== data.id) {
    toast(`New message from ${data.opponentPhone}`);
  }
}

function changePageTitle(count: number): void {
  if (count > 0) {
    document.title = `(${count}) SMS chat`;
  } else {
    document.title = "SMS chat";
  }
}

function* chatsWatchers(): Generator<Effect | Generator, void, void> {
  yield all([
    takeEvery(chatActions.createChat, ({ payload }: PayloadAction<ChatCreateData>) => createChat(payload)),
    takeEvery(chatActions.updateChat, ({ payload }: PayloadAction<UpdateChat>) => updateChatSaga(payload)),
    takeEvery(chatActions.switchCurrentChatArchive, ({ payload }: PayloadAction<SwitchChatArchiveData>) =>
      switchChatToArchive(payload)
    ),
    takeEvery(chatActions.switchSelectChat, ({ payload }: PayloadAction<string | null>) =>
      handleChangeCurrentChat(payload)
    ),
    takeEvery(chatEntryActions.handleConnectSocketChat, () => initChats()),
    takeEvery(
      [chatEntryActions.handleCreateChat, chatEntryActions.handleUpdateChat],
      ({ payload }: PayloadAction<CurrentChatData>) => displayNotifyNewMessage(payload)
    ),
    takeEvery(
      [chatEntryActions.fetchCountAllNotificationsSuccess, chatEntryActions.handleCountAllNotification],
      ({ payload }: PayloadAction<number>) => changePageTitle(payload)
    )
  ]);
}

export function* chatsServices(): Generator<Effect | Generator, void, void> {
  yield all([fork(chatsWatchers), fork(connectSocketChatSaga)]);
}
